import { useLingui } from '@lingui/react/macro'
import type { MouseEvent } from 'react'
import { GitCompareArrowsIcon } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Link, useLocation } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/lib/app-store'
import { resolveRedirectTarget } from '@/lib/redirect-target'

// Anonymous-visitor CTA shown in the top bar (in place of the user menu): invites the viewer to sign in
// and compare their own profile against what they're looking at. `GitCompareArrowsIcon` is the same icon
// the in-report "Compare with another profile" action uses, so the concept reads consistently.
export default function CompareButton() {
  const { t } = useLingui()
  const customize = useAppStore((state) => state.store.customize)
  // The profile page the visitor is currently looking at. We carry it through auth as `?redirect`
  // (login + the whole register/test onboarding forward it) so they land back here — now able to
  // compare against their own profile — once they finish signing in or registering.
  const location = useLocation()
  const redirect = resolveRedirectTarget(location.href)

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!customize.loginUrl) return

    event.preventDefault()
    window.parent.postMessage(
      {
        type: 'danchiano_redirect',
        url: customize.loginUrl,
      },
      '*',
    )
  }

  const content = (
    <>
      <GitCompareArrowsIcon className="size-4" />
      {t`Compare my profile`}
    </>
  )

  // The "compare" brand orange (same hue as the comparison line in the radar) so the CTA reads as the
  // compare action rather than a generic primary button. Dark warm-brown text/icon (not white): white on
  // #f60 is only ~2.9:1 and fails WCAG AA, whereas this brown clears 4.5:1 in both the rest and hover
  // states while staying in the orange family.
  const compareClassName =
    'bg-[var(--color-radar-compare)] font-semibold text-[#331400] hover:bg-[var(--color-radar-compare)]/90'

  // SDK embeds redirect to the host app's own login via postMessage. We keep a plain <a> (not <Link>)
  // because <Link> doesn't navigate inside SDK iframes.
  if (customize.loginUrl) {
    return (
      <a href={customize.loginUrl} onClick={handleClick} className={cn(buttonVariants(), compareClassName)}>
        {content}
      </a>
    )
  }

  return (
    <Button asChild className={compareClassName}>
      <Link to="/login" search={{ redirect }}>
        {content}
      </Link>
    </Button>
  )
}
